import { useState } from "react";
import { Star } from "lucide-react";
import { RatingAxisFields } from "./FacilityRatings";
import { ratingDraftComplete, draftFromOwnRating } from "../lib/facilityRatings";

/**
 * Issue #581 — rate the field or net you just picked, from inside the
 * log-a-match / training-session form, instead of Martin's four-step detour
 * (log → leave the form → find the ground on the map → expand the surface →
 * rate).
 *
 * REUSE, NOT A SECOND MECHANISM. The chips are the grounds map's own
 * `RatingAxisFields`, driven by the same `axesForSurface` axis list and the
 * same `ratingBodyFromDraft` write body — Turn & Grip therefore shows and
 * travels on turf only, exactly as on the map, with no per-surface rule
 * copied here. This component captures a DRAFT and nothing more: it never
 * calls the API. The form owns the save, because the played-here gate
 * (`userHasPlayedAtGround`) 403s a rating cast before the match/session row
 * exists — so on a new entry the form POSTs first, then PUTs the rating with
 * the id off the saved row. See each form's submit handler.
 *
 * WHEN IT RENDERS NOTHING, which is the common case and the point:
 *   - no specific surface chosen ("Not sure", or a ground with no registered
 *     surfaces) — there is nothing to rate, and the form logs exactly as it
 *     does today;
 *   - the surface could not be named (a private ground opened for editing,
 *     whose facility list the caller cannot read) — no guessing an axis set.
 *
 * OPTIONAL AND SECONDARY. Collapsed by default; the form's primary job is
 * logging the match/session, and skipping the rating saves it unchanged.
 */
export default function InlineFacilityRating({
  facility,
  draft,
  setDraft,
  /** "match" | "session" — what the rating rides along with, for the copy. */
  savedWith = "match",
  disabled = false,
  testId = "inline-facility-rating",
  /** #1512 — "saved" draws the log flow's chips; absent keeps the default look. */
  tone,
}) {
  const [open, setOpen] = useState(false);

  // Nothing to rate — the form behaves exactly as it did before #581.
  if (!facility || facility.archived_at) return null;

  const surface = facility.surface;
  // #544's vocabulary: the kind in words where somebody has said what the
  // surface is, a neutral "surface" where nobody has — never a guess.
  const noun = facility.kind === "net" ? "net" : facility.kind === "field" ? "field" : "surface";
  const complete = ratingDraftComplete(draft);

  if (!open) {
    return (
      <div className="mt-2" data-testid={`${testId}-block`}>
        <button
          type="button"
          data-testid={`${testId}-open`}
          onClick={() => setOpen(true)}
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 min-h-11 text-[11px] font-semibold text-slate-600 hover:border-slate-400 disabled:opacity-60"
        >
          <Star size={11} />
          {complete ? `Rating ready — edit this ${noun}` : `Rate this ${noun} (optional)`}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-md border border-slate-200 p-2" data-testid={`${testId}-block`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
          Rate this {noun}
        </span>
        <button
          type="button"
          data-testid={`${testId}-collapse`}
          onClick={() => setOpen(false)}
          disabled={disabled}
          className="text-[10px] font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-60"
        >
          Hide
        </button>
      </div>
      <p className="mt-0.5 mb-1 text-[10px] text-slate-400">
        Optional — saved when you save the {savedWith}.
      </p>
      <RatingAxisFields
        surface={surface}
        draft={draft}
        setDraft={setDraft}
        saving={disabled}
        testId={`${testId}-editor`}
        tone={tone}
      />
      {complete && (
        <button
          type="button"
          data-testid={`${testId}-clear`}
          onClick={() => setDraft(draftFromOwnRating(null))}
          disabled={disabled}
          className="mt-1 text-[10px] font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-60"
        >
          Clear rating
        </button>
      )}
    </div>
  );
}
